import { Button } from 'antd';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { SharedScreenName } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { appOverride } from '../../util/AppUtils';
import * as icons from '../assets/images/Icons';
import { AppLogos, nervaLogoHeaderWhite } from '../assets/images/Logos';
import { PrivacyChoicesButton } from '../ConsentPreferences';
import { useFlowNavigator } from '../flows/FlowNavigation';
import { AppContainer } from './AppContainer';

export const Header: FunctionComponent = (props) => {
  const headerLogo = appOverride({ nerva: nervaLogoHeaderWhite, default: AppLogos.header });

  return (
    <AppHeader>
      <AppContainer style={{ textAlign: 'left', position: 'relative', width: '100%' }}>
        <TopHeader>
          <BackButton />
          {appOverride({
            nerva: (
              <>
                <MedicalLogoContainer>
                  <LogoText src={headerLogo} alt="Nerva" />
                </MedicalLogoContainer>
                <NervaHeaderActions>
                  <AlreadySignedUpButton />
                  <StyledPrivacyChoicesButton />
                </NervaHeaderActions>
              </>
            ),
            default: (
              <DefaultHeaderActions>
                <StyledPrivacyChoicesButton />
                <HeaderLogo alt={'Logo'} src={headerLogo} />
              </DefaultHeaderActions>
            ),
          })}
        </TopHeader>
        {props.children}
      </AppContainer>
    </AppHeader>
  );
};

const BackButton: FunctionComponent = () => {
  const pageStack = useAppStore(AppSelector.pageStack);
  const canGoBack = pageStack.length > 1;
  const navigation = useFlowNavigator();

  if (!canGoBack) return <div style={{ height: 21 }} />;

  return (
    <div>
      {appOverride({
        nerva: (
          <BackPillButton type="link" onClick={navigation.goBack}>
            <BackArrowIcon src={icons.chevronLeft} alt="" />
            Back
          </BackPillButton>
        ),
        default: (
          <StyledBackButton
            type="link"
            onClick={navigation.goBack}
            icon={<BackArrowIcon src={icons.backBlack} alt="Back arrow icon" />}
          />
        ),
      })}
    </div>
  );
};

const AlreadySignedUpButton: FunctionComponent = () => {
  const pageStack = useAppStore(AppSelector.pageStack);
  const user = useAppStore(AppSelector.user);
  const navigation = useFlowNavigator();

  // A reset history also leaves a single page, so check the user is not already signed in — the
  // prompt makes no sense on the screens that follow registration.
  const showButton = pageStack.length === 1 && user.userId === undefined;

  if (!showButton) return null;

  return (
    <StyledAlreadySignedUpButton onClick={() => navigation.goTo(SharedScreenName.Auth)}>
      Already signed up?
    </StyledAlreadySignedUpButton>
  );
};

const AppHeader = styled.div`
  color: ${Color.decorative.accent};
  padding: ${({ theme }) => `${theme.spacing._8px} 0 0 0`};
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  position: ${appOverride({ nerva: 'sticky', default: 'relative' })};
  top: ${appOverride({ nerva: '0', default: 'auto' })};
  z-index: 2;
  ${appOverride({
    nerva: `
      background: rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(12px);
      padding-bottom: 8px;
    `,
    default: '',
  })}
`;

const TopHeader = styled.div`
  display: flex;
  height: ${({ theme }) => theme.spacing._40px};
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const StyledAlreadySignedUpButton = styled.button`
  color: white;
  text-decoration: underline;
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const StyledPrivacyChoicesButton = styled(PrivacyChoicesButton)`
  color: ${appOverride({ nerva: 'white', default: Color.action.default })};
  text-decoration: underline;
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const NervaHeaderActions = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing._2px};

  @media (min-width: 600px) {
    right: calc(167.5px - 35vw);
  }
`;

const DefaultHeaderActions = styled.div`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
`;

const MedicalLogoContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._8px};
`;

const LogoText = styled.img`
  height: ${({ theme }) => theme.spacing._30px};
`;

const HeaderLogo = styled.img`
  height: ${({ theme }) => theme.spacing._30px};
  margin-top: ${({ theme }) => theme.spacing._2px};
`;

const BackArrowIcon = styled.img`
  filter: ${appOverride({ nerva: 'brightness(0) invert(1)', default: 'none' })};
`;

// The pill control from the Nerva app: chevron plus a "Back" label inside a translucent outline.
const BackPillButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._6px};
  height: ${({ theme }) => theme.spacing._32px};
  padding: ${({ theme }) => `0 ${theme.spacing._12px}`};
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  color: white;
  font-size: ${({ theme }) => theme.fontSize.xxs};

  &:hover,
  &:focus {
    color: white;
    border-color: rgba(255, 255, 255, 0.7);
  }

  img {
    width: ${({ theme }) => theme.spacing._12px};
    height: ${({ theme }) => theme.spacing._12px};
  }
`;

const StyledBackButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing._6px} ${theme.spacing._6px} ${theme.spacing._6px} 0`};
  display: inline-flex;
  align-items: center;
  height: ${({ theme }) => theme.spacing._28px};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.spacing._24px};
`;
